// MONITORING-P1 (docs/design/2026-11-01-monitoring-in-companyos.md): ClawPanel deploys,
// CompanyOS watches. The telemetry a DE's bridge ships arrives as `telemetry` items on the ingest
// route (`ingest.post`); these routes read it back and carry stops the other way.
//
//   GET  runs/{id}/telemetry          people who may view the run: its timeline events, its
//                                     jobs (stages, slide scores, redos, cost, delivery), its stop
//                                     requests and its alerts
//   POST runs/{id}/stop               people who may view the run: queue a stop for the DE's bridge
//                                     (audited in cp.platform_audit `run.stop_requested`)
//   POST ingest/stop-requests/poll    the DE's ingest key (`ingest:telemetry`): the stops waiting
//                                     for its runs; the bridge answers each with a `stop.acked`
//                                     telemetry event
//   GET  monitoring/alerts            managers: stuck runs, post storms, stalled job stages,
//                                     tripped guards
//   PATCH monitoring/alerts/{id}      managers: ack or resolve one
//   GET/POST/PATCH operator/guards    platform admins only: the guard rules
//   GET  monitoring/fleet             MONITORING-P2, managers: per DE seat, where its bridge runs,
//                                     the bridge version, last heartbeat and event, telemetry
//                                     on/off, open alerts
//   GET  monitoring/budget-incidents  MONITORING-P2, managers: budget incidents per DE and period
//   GET/PUT operator/skill-fingerprints  platform admins only: skill-leak guard fingerprints
import { z } from "zod";
import {
  BudgetIncidentKind,
  BudgetPeriodType,
  BudgetSubjectKind,
  GuardAction,
  GuardMatchKind,
  MonitorAlertKind,
  MonitorAlertSeverity,
  MonitorAlertState,
  MonitorJobStatus,
  RunStopRequestSource,
  RunStopRequestState,
  TelemetryLevel,
} from "../enums";
import { Decimal, MinorAmount, Timestamp, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const agents = { feature: "agents" } as const;

export const TelemetryEventView = z
  .object({
    id: Uuid,
    type: z.string(),
    source: z.string(),
    seq: z.int(),
    level: TelemetryLevel,
    run_id: Uuid.nullable(),
    job_id: z.string().nullable(),
    message: z.string().nullable(),
    data: z.record(z.string(), z.unknown()),
    redactions: z.int(),
    occurred_at: Timestamp,
    received_at: Timestamp,
  })
  .meta({ id: "TelemetryEventView" });
export type TelemetryEventView = z.infer<typeof TelemetryEventView>;

export const MonitorJobStageView = z.object({
  stage: z.string(),
  attempt: z.int(),
  started_at: Timestamp,
  ended_at: Timestamp.nullable(),
  ok: z.boolean().nullable(),
  budget_s: z.int().nullable(),
});

export const MonitorJobSlideView = z.object({
  slide: z.int(),
  score: z.number().nullable(),
  critic: z.string().nullable(),
  pass: z.boolean().nullable(),
  redos: z.int(),
});

export const MonitorJobView = z
  .object({
    job_id: z.string(),
    run_id: Uuid.nullable(),
    de_membership_id: Uuid,
    kind: z.string().nullable(),
    status: MonitorJobStatus,
    stage: z.string().nullable(),
    stages: z.array(MonitorJobStageView),
    slides_total: z.int().nullable(),
    slides_done: z.int(),
    slides: z.array(MonitorJobSlideView),
    score_min: z.number().nullable(),
    score_avg: z.number().nullable(),
    redos: z.int(),
    cost_micros: z.int(),
    tokens_in: z.int(),
    tokens_out: z.int(),
    delivery: z
      .object({
        thread_id: z.string().nullable(),
        files: z.int().nullable(),
        post_ids: z.array(z.string()),
        at: Timestamp,
      })
      .nullable(),
    started_at: Timestamp,
    ended_at: Timestamp.nullable(),
    last_event_at: Timestamp,
  })
  .meta({ id: "MonitorJobView" });
export type MonitorJobView = z.infer<typeof MonitorJobView>;

export const RunStopRequestView = z
  .object({
    id: Uuid,
    run_id: Uuid,
    de_membership_id: Uuid,
    source: RunStopRequestSource,
    requested_by_membership_id: Uuid.nullable(),
    guard_rule_id: Uuid.nullable(),
    reason: z.string(),
    state: RunStopRequestState,
    requested_at: Timestamp,
    expires_at: Timestamp,
    picked_at: Timestamp.nullable(),
    acked_at: Timestamp.nullable(),
    ack: z
      .object({
        ok: z.boolean(),
        method: z.string().nullable(),
        latency_ms: z.int().nullable(),
        message: z.string().nullable(),
      })
      .nullable(),
  })
  .meta({ id: "RunStopRequestView" });
export type RunStopRequestView = z.infer<typeof RunStopRequestView>;

export const MonitorAlertView = z
  .object({
    id: Uuid,
    kind: MonitorAlertKind,
    severity: MonitorAlertSeverity,
    state: MonitorAlertState,
    de_membership_id: Uuid.nullable(),
    run_id: Uuid.nullable(),
    job_id: z.string().nullable(),
    guard_rule_id: Uuid.nullable(),
    summary: z.string(),
    evidence: z.record(z.string(), z.unknown()),
    opened_at: Timestamp,
    last_seen_at: Timestamp,
    acked_by_membership_id: Uuid.nullable(),
    acked_at: Timestamp.nullable(),
    resolved_at: Timestamp.nullable(),
    resolved_how: z.string().nullable(),
  })
  .meta({ id: "MonitorAlertView" });
export type MonitorAlertView = z.infer<typeof MonitorAlertView>;

export const RunTelemetry = z
  .object({
    run_id: Uuid,
    /** Oldest first; at most `limit` (500), `truncated` when there were more. */
    events: z.array(TelemetryEventView),
    truncated: z.boolean(),
    jobs: z.array(MonitorJobView),
    stop_requests: z.array(RunStopRequestView),
    alerts: z.array(MonitorAlertView),
  })
  .meta({ id: "RunTelemetry" });
export type RunTelemetry = z.infer<typeof RunTelemetry>;

export const RunStopCreate = z
  .strictObject({ reason: z.string().trim().min(1).max(500).optional() })
  .meta({ id: "RunStopCreate" });
export type RunStopCreate = z.infer<typeof RunStopCreate>;

/** What the bridge gets back from a poll: the stops to carry out now, oldest first. */
export const StopRequestPoll = z
  .object({
    data: z.array(
      z.object({
        id: Uuid,
        /** The CompanyOS run id (the bridge maps it to the Hermes run it checked out). */
        run_id: Uuid,
        reason: z.string(),
        requested_at: Timestamp,
        expires_at: Timestamp,
      }),
    ),
    /** Poll again after this many seconds. */
    poll_after_s: z.int(),
  })
  .meta({ id: "StopRequestPoll" });
export type StopRequestPoll = z.infer<typeof StopRequestPoll>;

export const MonitorAlertListQuery = z.object({
  state: z.enum(["active", "open", "acked", "resolved", "all"]).optional(),
  kind: MonitorAlertKind.optional(),
  de_membership_id: Uuid.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});

export const MonitorAlertPage = z
  .object({ data: z.array(MonitorAlertView) })
  .meta({ id: "MonitorAlertPage" });

export const MonitorAlertPatch = z
  .strictObject({ state: z.enum(["acked", "resolved"]) })
  .meta({ id: "MonitorAlertPatch" });
export type MonitorAlertPatch = z.infer<typeof MonitorAlertPatch>;

/** MONITORING-P2: one DE seat on the fleet page. */
export const FleetRow = z
  .object({
    de_membership_id: Uuid,
    de_name: z.string(),
    seat_id: Uuid.nullable(),
    seat_title: z.string().nullable(),
    /** The host or satellite name from the bridge's heartbeat; null until one names it. */
    host: z.string().nullable(),
    /** The last event's `source` (`bridge`, `job`, …); null when the DE never shipped one. */
    source: z.string().nullable(),
    bridge_version: z.string().nullable(),
    /** The other DE keys the same bridge serves (its host's profiles). */
    de_keys: z.array(z.string()),
    /** From the last heartbeat; null: no heartbeat yet (events alone count as on). */
    telemetry_on: z.boolean().nullable(),
    uptime_s: z.int().nullable(),
    last_heartbeat_at: Timestamp.nullable(),
    last_event_at: Timestamp.nullable(),
    last_event_type: z.string().nullable(),
    /** Runs queued or running now: while there are any, silence is an alert. */
    live_runs: z.int(),
    open_alerts: z.int(),
    open_alert_kinds: z.array(MonitorAlertKind),
    critical: z.boolean(),
  })
  .meta({ id: "FleetRow" });
export type FleetRow = z.infer<typeof FleetRow>;

export const FleetPage = z
  .object({
    data: z.array(FleetRow),
    /** The company's `bridge_silent` limit in minutes (default 30). */
    silent_after_minutes: z.int(),
  })
  .meta({ id: "FleetPage" });
export type FleetPage = z.infer<typeof FleetPage>;

/** MONITORING-P2: one `budget_incident` row with its budget and the DE it belongs to. */
export const BudgetIncidentView = z
  .object({
    id: Uuid,
    kind: BudgetIncidentKind,
    threshold: Decimal.nullable(),
    period_key: z.string().nullable(),
    amount_minor: MinorAmount,
    currency: z.string(),
    budget_id: Uuid,
    budget_amount_minor: MinorAmount,
    budget_period_type: BudgetPeriodType,
    budget_subject_kind: BudgetSubjectKind,
    /** The DE: its run's, its own budget's, its agent's, or its seat's while it held the seat. */
    de_membership_id: Uuid.nullable(),
    de_name: z.string().nullable(),
    run_id: Uuid.nullable(),
    resolved_by_approval_id: Uuid.nullable(),
    resolved_at: Timestamp.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "BudgetIncidentView" });
export type BudgetIncidentView = z.infer<typeof BudgetIncidentView>;

export const BudgetIncidentListQuery = z.object({
  de_membership_id: Uuid.optional(),
  period_key: z.string().min(1).max(32).optional(),
  state: z.enum(["open", "resolved", "all"]).optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
});

export const BudgetIncidentPage = z
  .object({ data: z.array(BudgetIncidentView) })
  .meta({ id: "BudgetIncidentPage" });

export const GuardRuleView = z
  .object({
    id: Uuid,
    company_id: Uuid.nullable(),
    de_membership_id: Uuid.nullable(),
    match_kind: GuardMatchKind,
    pattern: z.string(),
    action: GuardAction,
    enabled: z.boolean(),
    builtin: z.boolean(),
    note: z.string().nullable(),
    version: z.int(),
    created_by_user_id: Uuid.nullable(),
    updated_by_user_id: Uuid.nullable(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .meta({ id: "GuardRuleView" });
export type GuardRuleView = z.infer<typeof GuardRuleView>;

export const GuardRuleList = z
  .object({ data: z.array(GuardRuleView) })
  .meta({ id: "GuardRuleList" });

const GuardPattern = z.string().min(1).max(200).regex(/^\S+$/, "no spaces");

/** A new rule always starts as `alert`; an edit raises it to `stop_and_alert`. */
export const GuardRuleCreate = z
  .strictObject({
    company_id: Uuid.nullable().optional(),
    de_membership_id: Uuid.nullable().optional(),
    match_kind: GuardMatchKind,
    pattern: GuardPattern,
    note: z.string().max(300).nullable().optional(),
  })
  .refine((b) => !b.de_membership_id || b.company_id, {
    message: "a DE rule names its company",
    path: ["company_id"],
  })
  .meta({ id: "GuardRuleCreate" });
export type GuardRuleCreate = z.infer<typeof GuardRuleCreate>;

export const GuardRulePatch = z
  .strictObject({
    pattern: GuardPattern.optional(),
    action: GuardAction.optional(),
    enabled: z.boolean().optional(),
    note: z.string().max(300).nullable().optional(),
  })
  .meta({ id: "GuardRulePatch" });
export type GuardRulePatch = z.infer<typeof GuardRulePatch>;

// SKILL-LEAK-GUARD: one skill pack version's fingerprints (domain-agents `skill-guard`
// `PackFingerprint`): hashes only. Anything that is not packed hashes is refused, so no skill
// text can be registered by mistake.
const PackedHashes = z
  .string()
  .max(4_000_000)
  .regex(/^[A-Za-z0-9+/]*={0,2}$/, "packed hashes (base64)");
export const SkillFingerprintFileBody = z.strictObject({
  path: z.string().min(1).max(300),
  n_words: z.int().min(0).max(1_000_000),
  n_chars: z.int().min(0).max(1_000_000),
  words: PackedHashes,
  chars: PackedHashes,
});
export const SkillFingerprintPut = z
  .strictObject({
    company_id: Uuid.nullable().optional(),
    fingerprint: z.strictObject({
      format: z.literal("cos-skill-fp/1"),
      pack_key: z
        .string()
        .max(63)
        .regex(/^[a-z0-9][a-z0-9-]{0,62}$/),
      pack_version: z.string().min(1).max(40),
      files: z.array(SkillFingerprintFileBody).max(2000),
      canaries: z
        .array(
          z
            .string()
            .max(18)
            .regex(/^\d{1,3}:[0-9a-f]{14}$/, "a canary hash"),
        )
        .max(500),
    }),
  })
  .meta({ id: "SkillFingerprintPut" });
export type SkillFingerprintPut = z.infer<typeof SkillFingerprintPut>;

/** What the list shows of a registration: names and counts, never the hashes. */
export const SkillFingerprintView = z
  .object({
    id: Uuid,
    company_id: Uuid.nullable(),
    pack_key: z.string(),
    pack_version: z.string(),
    format: z.string(),
    files: z.int(),
    shingles: z.int(),
    canaries: z.int(),
    version: z.int(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .meta({ id: "SkillFingerprintView" });
export type SkillFingerprintView = z.infer<typeof SkillFingerprintView>;

export const SkillFingerprintList = z
  .object({ data: z.array(SkillFingerprintView) })
  .meta({ id: "SkillFingerprintList" });

const operator = { feature: "core", tag: "operator", access: "operator" } as const;

export const monitoringRoutes = {
  "runs.telemetry": defineRoute({
    ...agents,
    id: "runs.telemetry",
    method: "get",
    path: `${base}/runs/{id}/telemetry`,
    tag: "runs",
    summary: "A run's telemetry: timeline events, jobs, stop requests and alerts",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: RunTelemetry },
    errors: [],
    emits: [],
  }),
  "runs.requestStop": defineRoute({
    ...agents,
    id: "runs.requestStop",
    method: "post",
    path: `${base}/runs/{id}/stop`,
    tag: "runs",
    summary: "Ask the DE's bridge to stop a run on its host (people who may view the run)",
    description:
      "Queues one stop request for the run (a second press returns the live one); the DE's " +
      "bridge picks it up on its next poll and answers with a `stop.acked` telemetry event. " +
      "Expires after 5 minutes unpicked. Audited as `run.stop_requested` in the platform audit. " +
      "It does not end the run's record: cancel does that.",
    access: "member",
    params: CompanyItemParams,
    body: RunStopCreate,
    idempotency: "optional",
    // audited in cp.platform_audit, not the company's event log
    safe: true,
    response: { status: 200, schema: RunStopRequestView },
    errors: ["not_found", "run_state_conflict"],
    emits: [],
  }),
  "ingest.stopRequests": defineRoute({
    feature: "core",
    id: "ingest.stopRequests",
    method: "post",
    path: `${base}/ingest/stop-requests/poll`,
    tag: "ingest",
    summary: "The stop requests waiting for the key's DE (its bridge polls every 20 s)",
    description:
      "Needs `ingest:telemetry`. Each request is returned until it is acked (`stop.acked`) or " +
      "expires; the first poll that returns it marks it picked.",
    access: "ingest_key",
    params: CompanyParams,
    safe: true,
    response: { status: 200, schema: StopRequestPoll },
    errors: ["scope_missing"],
    emits: [],
  }),
  "monitoring.alerts.list": defineRoute({
    ...agents,
    id: "monitoring.alerts.list",
    method: "get",
    path: `${base}/monitoring/alerts`,
    tag: "runs",
    summary: "Monitoring alerts: stuck runs, post storms, stalled job stages, tripped guards",
    access: "manager",
    params: CompanyParams,
    query: MonitorAlertListQuery,
    response: { status: 200, schema: MonitorAlertPage },
    errors: [],
    emits: [],
  }),
  "monitoring.alerts.update": defineRoute({
    ...agents,
    id: "monitoring.alerts.update",
    method: "patch",
    path: `${base}/monitoring/alerts/{id}`,
    tag: "runs",
    summary: "Ack or resolve a monitoring alert",
    access: "manager",
    params: CompanyItemParams,
    body: MonitorAlertPatch,
    safe: true,
    response: { status: 200, schema: MonitorAlertView },
    errors: ["not_found"],
    emits: [],
  }),
  "monitoring.fleet": defineRoute({
    ...agents,
    id: "monitoring.fleet",
    method: "get",
    path: `${base}/monitoring/fleet`,
    tag: "runs",
    summary: "The fleet: per DE seat, where its bridge runs, its version, last word and alerts",
    description:
      "From the bridges' own telemetry (`bridge.heartbeat` and every event's `source`), not the " +
      "satellite registry.",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: FleetPage },
    errors: [],
    emits: [],
  }),
  "monitoring.budgetIncidents": defineRoute({
    // budgets are core (the seat drawer's Finance tab links here with the add-on off too)
    feature: "core",
    id: "monitoring.budgetIncidents",
    method: "get",
    path: `${base}/monitoring/budget-incidents`,
    tag: "runs",
    summary: "Budget incidents (thresholds crossed, caps hit, refused reservations) per DE",
    access: "manager",
    params: CompanyParams,
    query: BudgetIncidentListQuery,
    response: { status: 200, schema: BudgetIncidentPage },
    errors: [],
    emits: [],
  }),
  "operator.guards.list": defineRoute({
    ...operator,
    id: "operator.guards.list",
    method: "get",
    path: "/v1/operator/guards",
    summary: "The guard rules over DE telemetry (platform admins)",
    response: { status: 200, schema: GuardRuleList },
    errors: [],
    emits: [],
  }),
  "operator.guards.create": defineRoute({
    ...operator,
    id: "operator.guards.create",
    method: "post",
    path: "/v1/operator/guards",
    summary: "Add a guard rule (starts alert-only; platform admins)",
    body: GuardRuleCreate,
    // platform policy, versioned in cp.platform_audit (`guard_rule.created`)
    safe: true,
    response: { status: 201, schema: GuardRuleView },
    errors: ["validation_failed"],
    emits: [],
  }),
  "operator.guards.update": defineRoute({
    ...operator,
    id: "operator.guards.update",
    method: "patch",
    path: "/v1/operator/guards/{id}",
    summary: "Change a guard rule: its pattern, action, note, or switch it off (platform admins)",
    params: z.object({ id: Uuid }),
    body: GuardRulePatch,
    // platform policy, versioned in cp.platform_audit (`guard_rule.changed`)
    safe: true,
    response: { status: 200, schema: GuardRuleView },
    errors: ["not_found", "validation_failed"],
    emits: [],
  }),
  "operator.skillFingerprints.list": defineRoute({
    ...operator,
    id: "operator.skillFingerprints.list",
    method: "get",
    path: "/v1/operator/skill-fingerprints",
    summary: "The registered skill fingerprints, names and counts only (platform admins)",
    response: { status: 200, schema: SkillFingerprintList },
    errors: [],
    emits: [],
  }),
  "operator.skillFingerprints.put": defineRoute({
    ...operator,
    id: "operator.skillFingerprints.put",
    method: "put",
    path: "/v1/operator/skill-fingerprints",
    summary:
      "Register or replace one skill pack version's fingerprints, platform-wide or for one company (platform admins)",
    body: SkillFingerprintPut,
    // platform policy, audited in cp.platform_audit (`skill_fingerprint.registered`)
    safe: true,
    response: { status: 200, schema: SkillFingerprintView },
    errors: ["validation_failed"],
    emits: [],
  }),
} as const;
